<template>
  <div class="px-4 pb-4 pt-1">
    <p class="text-textPrimary mb-4 text-left">
      Selecione um modelo para importar as configurações
    </p>

    <SearchInput
      v-model="searchQuery"
      label="Buscar unidades, escalas ou modelos"
      placeholder="Digite o nome da unidade, escala ou modelo"
      persistent-placeholder
      :search-function="handleSearch"
    />

    <div class="max-h-[320px] overflow-y-auto flex flex-col gap-4">
      <div v-for="unit in filteredUnits" :key="unit.id">
        <div class="mb-3 text-left text-textPrimary">
          <span class="text-xs font-bold">Unidade</span>
          <p class="text-sm">{{ unit.name }}</p>
        </div>

        <div class="flex flex-col gap-3">
          <div v-for="schedule in unit.schedules" :key="schedule.id">
            <div class="mb-2 text-left text-textPrimary pl-2">
              <span class="text-xs font-bold">Escala</span>
              <p class="text-sm">{{ schedule.name }}</p>
            </div>

            <div class="flex flex-col gap-2 pl-4">
              <div
                v-for="template in schedule.templates"
                :key="template.id"
                class="flex items-center rounded-lg border px-4 py-3 cursor-pointer transition-colors"
                :class="
                  modelValue === template.id
                    ? 'bg-[#E8F4F7] border border-primary'
                    : 'bg-[#F0F0F4] hover:bg-gray-200'
                "
                @click="$emit('update:modelValue', template.id)"
              >
                <RadioButton
                  :id="`template-${template.id}`"
                  name="template-selection"
                  :value="template.id"
                  :model-value="modelValue ?? ''"
                  @update:model-value="$emit('update:modelValue', Number($event))"
                >
                  <div class="text-left">
                    <span class="text-xs font-bold text-textPrimary">Modelo</span>
                    <p class="text-textPrimary">{{ template.name }}</p>
                  </div>
                </RadioButton>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p v-if="filteredUnits.length === 0" class="text-sm text-gray-500 text-center py-6">
        Nenhum modelo encontrado
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import SearchInput from '@/components/designSystem/Inputs/SearchInput.vue'
import RadioButton from '@/components/designSystem/RadioButton/index.vue'
import type { GroupedTemplatesResponse } from '@/Network/Types/Responses/ApprovalFlowTemplateResponses'

interface TemplateItem {
  id: number
  name: string
}

interface ScheduleWithTemplates {
  id: number
  name: string
  templates: TemplateItem[]
}

interface UnitWithSchedules {
  id: number
  name: string
  schedules: ScheduleWithTemplates[]
}

const props = defineProps<{
  modelValue: number | null
  groupedData?: GroupedTemplatesResponse[]
}>()

defineEmits<{
  (e: 'update:modelValue', value: number | null): void
}>()

const searchQuery = ref('')

const units = computed<UnitWithSchedules[]>(() => {
  return (props.groupedData ?? []).map((group) => ({
    id: group.healthUnit.id,
    name: group.healthUnit.name,
    schedules: group.schedules.map((s) => ({
      id: s.schedule.id,
      name: s.schedule.name,
      templates: s.templates.map((t) => ({
        id: t.id,
        name: t.name
      }))
    }))
  }))
})

const filteredUnits = computed(() => {
  if (!searchQuery.value) return units.value
  const q = searchQuery.value.toLowerCase()
  return units.value
    .map((unit) => ({
      ...unit,
      schedules: unit.schedules
        .map((s) => ({
          ...s,
          templates: s.templates.filter(
            (t) =>
              t.name.toLowerCase().includes(q) ||
              s.name.toLowerCase().includes(q) ||
              unit.name.toLowerCase().includes(q)
          )
        }))
        .filter((s) => s.templates.length > 0)
    }))
    .filter((unit) => unit.schedules.length > 0)
})

const handleSearch = async () => {
  // filtro local via computed
}
</script>
